// components/partial/DeleteOrLossModal.jsx
import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

class DeleteOrLossModal extends Component {
  state = {
    actionType: null, // 'delete', 'loss', or 'reject'
    lossReason: '',
    lossNotes: '',
    showReasonFields: false
  };

  // Loss reason options for dropdown (alphabetical, "Other" last)
  lossReasons = [
    { value: 'customer_cancelled', label: 'Customer cancelled service' },
    { value: 'customer_chose_competitor', label: 'Customer chose competitor' },
    { value: 'found_internal_solution', label: 'Customer found internal solution' },
    { value: 'no_response', label: 'Customer stopped responding' },
    { value: 'duplicate_request', label: 'Duplicate/mistake request' },
    { value: 'price_too_high', label: 'Price too high' },
    { value: 'service_unavailable', label: 'Service not available' },
    { value: 'timeline_issue', label: 'Timeline could not be met' },
    { value: 'other', label: 'Other reason' }
  ];

  // Referral loss reason options for dropdown (alphabetical, "Other" last)
  referralLossReasons = [
    { value: 'bad_contact_info', label: 'Bad contact info' },
    { value: 'chose_competitor', label: 'Chose a competitor' },
    { value: 'no_response', label: 'Customer not responding' },
    { value: 'not_interested', label: 'Customer not interested' },
    { value: 'duplicate_lead', label: 'Duplicate lead' },
    { value: 'out_of_area', label: 'Out of service area' },
    { value: 'service_unavailable', label: 'Service not available' },
    { value: 'other', label: 'Other reason' }
  ];

  // Rejection reason options for dropdown (alphabetical, "Other" last)
  rejectionReasons = [
    { value: 'pricing_too_low', label: 'Dealer pricing too low' },
    { value: 'location_too_far', label: 'Location too far' },
    { value: 'installer_unavailable', label: 'Not available / Fully booked' },
    { value: 'service_not_offered', label: 'Service not offered' },
    { value: 'vehicle_not_supported', label: 'Vehicle type not supported' },
    { value: 'wrong_vehicle', label: 'Wrong vehicle delivered' },
    { value: 'other', label: 'Other reason' }
  ];

  handleActionSelect = (type) => {
    this.setState({
      actionType: type,
      showReasonFields: type === 'loss' || type === 'reject'
    });
  };

  handleReasonChange = (e) => {
    this.setState({ lossReason: e.target.value });
  };

  handleNotesChange = (e) => {
    this.setState({ lossNotes: e.target.value });
  };

  handleConfirm = () => {
    const { actionType, lossReason, lossNotes } = this.state;
    const { mode } = this.props; // 'delete' or 'reject'

    if ((actionType === 'loss' || actionType === 'reject') && !lossReason) {
      const action = mode === 'reject' ? 'rejection' : 'loss';
      alert(`Please select a reason for the ${action}`);
      return;
    }

    const result = {
      confirmed: true,
      isLoss: actionType === 'loss',
      isReject: actionType === 'reject',
      reasonCode: actionType === 'loss' ? lossReason :
                  actionType === 'reject' ? lossReason :
                  'administrative_delete',
      reasonNotes: lossNotes || null
    };

    this.props.onConfirm(result);
    this.resetState();
  };

  handleCancel = () => {
    this.props.onHide();
    this.resetState();
  };

  resetState = () => {
    this.setState({
      actionType: null,
      lossReason: '',
      lossNotes: '',
      showReasonFields: false
    });
  };

  render() {
    const { show, request, mode = 'delete' } = this.props; // mode: 'delete', 'reject', or 'referral'
    const { actionType, lossReason, lossNotes, showReasonFields } = this.state;

    if (!request) return null;

    const isRejectMode = mode === 'reject';
    const isReferralMode = mode === 'referral';

    const vehicleInfo = isReferralMode
      ? `${request.Year || ''} ${request.Make || ''} ${request.Model || ''}`.trim()
      : `${request.vehicleYear} ${request.vehicleMake} ${request.vehicleModel}`;
    const hasInvoice = !isReferralMode && request.invoiceId && request.currentState === "finished";

    // Different text based on mode
    const title = isReferralMode ? 'Close Lead or Record Loss?' :
                  isRejectMode ? 'Confirm Rejection' : 'Delete Request or Record Loss?';
    const reasonLabel = isReferralMode ? 'Loss Reason' :
                        isRejectMode ? 'Rejection Reason' : 'Loss Reason';
    const reasonPlaceholder = isReferralMode ?
      'Any additional details about why this lead was lost...' :
      isRejectMode ?
      'Any additional details about why you are rejecting this...' :
      'Any additional details about why this opportunity was lost...';

    return (
      <Modal show={show} onHide={this.handleCancel} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="job-info mb-4 p-3 bg-light rounded">
            <h6 className="mb-1">{vehicleInfo}</h6>
            {isReferralMode ? (
              <>
                {request.customer_name && <p className="text-muted mb-1">{request.customer_name}</p>}
                <p className="mb-0"><strong>Referral ID:</strong> {request.referral_id}</p>
              </>
            ) : (
              <>
                <p className="text-muted mb-1">{request.serviceRequested}</p>
                <p className="mb-0"><strong>Request ID:</strong> {request.requestId}</p>
              </>
            )}
          </div>

          {hasInvoice && !isRejectMode && (
            <div className="alert alert-warning">
              <strong>Warning:</strong> This will also delete/void the invoice in Stripe.
            </div>
          )}

          {isReferralMode ? (
            // Referral mode: Delete vs Loss choice with referral-specific labels
            <div className="action-selection mb-4">
              <p className="font-weight-bold mb-3">What type of action is this?</p>

              <div className="action-buttons d-flex gap-3 mb-3">
                <Button
                  variant={actionType === 'delete' ? 'primary' : 'outline-primary'}
                  className="flex-fill"
                  onClick={() => this.handleActionSelect('delete')}
                >
                  <div className="text-left">
                    <div className="font-weight-bold">Just Delete</div>
                    <small className="text-muted">
                      Administrative cleanup, spam, or test entry
                    </small>
                  </div>
                </Button>

                <Button
                  variant={actionType === 'loss' ? 'danger' : 'outline-danger'}
                  className="flex-fill"
                  onClick={() => this.handleActionSelect('loss')}
                >
                  <div className="text-left">
                    <div className="font-weight-bold">Lead Lost</div>
                    <small className="text-muted">
                      Business opportunity lost — track for analysis
                    </small>
                  </div>
                </Button>
              </div>
            </div>
          ) : isRejectMode ? (
            // Reject mode: Simple confirmation with reason
            <div className="action-selection mb-4">
              <p className="font-weight-bold mb-3">
                Are you sure you want to reject this {request.requestType === 'dealer' ? 'dealer request' : 'job'}?
              </p>
              <Button
                variant={actionType === 'reject' ? 'danger' : 'outline-danger'}
                className="w-100 mb-3"
                onClick={() => this.handleActionSelect('reject')}
              >
                <div>
                  <div className="font-weight-bold">Yes, Reject This Job</div>
                  <small className="text-muted">
                    {request.requestType === 'dealer' ?
                      'Notify dealer you cannot take this job' :
                      'Mark as rejected (wrong vehicle, etc.)'}
                  </small>
                </div>
              </Button>
            </div>
          ) : (
            // Delete mode: Delete vs Loss choice
            <div className="action-selection mb-4">
              <p className="font-weight-bold mb-3">What type of action is this?</p>

              <div className="action-buttons d-flex gap-3 mb-3">
                <Button
                  variant={actionType === 'delete' ? 'primary' : 'outline-primary'}
                  className="flex-fill"
                  onClick={() => this.handleActionSelect('delete')}
                >
                  <div className="text-left">
                    <div className="font-weight-bold">Just Delete</div>
                    <small className="text-muted">
                      Administrative cleanup, duplicate, or mistake
                    </small>
                  </div>
                </Button>

                <Button
                  variant={actionType === 'loss' ? 'danger' : 'outline-danger'}
                  className="flex-fill"
                  onClick={() => this.handleActionSelect('loss')}
                >
                  <div className="text-left">
                    <div className="font-weight-bold">Customer Lost/Not Interested</div>
                    <small className="text-muted">
                      Business opportunity lost - track for analysis
                    </small>
                  </div>
                </Button>
              </div>
            </div>
          )}

          {showReasonFields && (
            <div className="loss-details border-top pt-3">
              <Form.Group>
                <Form.Label className="font-weight-bold">
                  {reasonLabel} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={lossReason}
                  onChange={this.handleReasonChange}
                  required
                >
                  <option value="">-- Select a reason --</option>
                  {(isReferralMode ? this.referralLossReasons : isRejectMode ? this.rejectionReasons : this.lossReasons).map(reason => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label className="font-weight-bold">
                  Additional Notes <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={lossNotes}
                  onChange={this.handleNotesChange}
                  placeholder={reasonPlaceholder}
                />
              </Form.Group>
            </div>
          )}

          {actionType === 'delete' && (
            <div className="alert alert-info mt-3">
              This will be recorded as an administrative deletion with no impact on win/loss statistics.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={this.handleCancel}>
            Cancel
          </Button>
          <Button
            variant={(actionType === 'loss' || actionType === 'reject') ? 'danger' : 'primary'}
            onClick={this.handleConfirm}
            disabled={!actionType}
          >
            {actionType === 'loss' ? 'Record Loss & Close' :
             actionType === 'reject' ? 'Confirm Rejection' :
             actionType === 'delete' ? (isReferralMode ? 'Delete Lead' : 'Delete Request') :
             isRejectMode ? 'Click button above to continue' : 'Select an option above'}
          </Button>
        </Modal.Footer>

        <style jsx>{`
          .action-buttons .btn {
            padding: 1rem;
            height: auto;
            min-height: 80px;
          }

          .action-buttons small {
            display: block;
            margin-top: 0.25rem;
            font-size: 0.8rem;
          }

          .gap-3 {
            gap: 1rem;
          }

          .job-info {
            border-left: 4px solid #007bff;
          }
        `}</style>
      </Modal>
    );
  }
}

export default DeleteOrLossModal;
